import React from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Typography,
  Box,
  Paper,
  Button,
  Grid,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  IconButton,
  CircularProgress,
  Divider,
  alpha
} from '@mui/material';
import {
  Close as CloseIcon,
  ReceiptLong as ReceiptIcon,
  Verified as VerifiedIcon,
  Print as PrintIcon
} from '@mui/icons-material';
import image1 from '../assets/riyal.png';

/**
 * InvoiceViewDialog - A premium styled component for displaying invoice details
 * 
 * @param {Object} props
 * @param {boolean} props.open - Controls whether the dialog is open
 * @param {Function} props.onClose - Function to call when dialog should close
 * @param {Object} props.invoice - The invoice data to display (contains master, details, and payments)
 * @param {boolean} props.loading - Whether the invoice data is still loading
 * @param {string} props.title - Optional custom title for the dialog
 * @param {string} props.currencySymbol - Optional currency symbol to use (defaults to SAR)
 */
const InvoiceViewDialog = ({
  open,
  onClose,
  invoice,
  loading,
  title = "Invoice Details",
  currencySymbol = "SAR",
  currencyIcon,
  onPrint
}) => {
  console.log('InvoiceViewDialog rendered with props:', {
    open,
    invoice,
    loading,
    title,
    currencySymbol
  });

  if (!open) return null;

  console.log('Invoice data structure:', invoice);
  console.log('Invoice is null or undefined?', invoice == null);
  if (invoice) {
    console.log('Invoice keys:', Object.keys(invoice));
    console.log('Invoice has items?', Array.isArray(invoice.items));
    if (invoice.items) {
      console.log('Items count:', invoice.items.length);
    }
  }

  // Premium color palette
  const colors = {
    primary: '#1e3a8a', // Deep blue
    secondary: '#64748b', // Slate gray
    accent: '#8b5cf6', // Purple
    gold: '#d4af37', // Gold
    background: '#f8fafc', // Light background
    paper: '#ffffff', // White
    border: '#e2e8f0', // Light border
  };

  // Styling for the invoice header with watermark
  const headerStyle = {
    background: `linear-gradient(135deg, ${colors.paper} 0%, ${alpha(colors.primary, 0.03)} 100%)`,
    borderBottom: `1px solid ${colors.border}`,
    position: 'relative',
    overflow: 'hidden',
    padding: 3,
  };

  // Watermark effect
  const watermarkStyle = {
    position: 'absolute',
    top: '50%',
    left: '50%',
    transform: 'translate(-50%, -50%) rotate(-30deg)',
    opacity: 0.03,
    fontSize: '10rem',
    color: colors.primary,
    fontWeight: 'bold',
    pointerEvents: 'none',
    textTransform: 'uppercase',
    whiteSpace: 'nowrap',
  };

  // Status chip styling based on payment status
  const getStatusChipProps = (status) => {
    const isPurchaseReturn = title?.toLowerCase().includes('purchase return') || title?.toLowerCase().includes('return');
    switch (status?.toLowerCase()) {
      case 'paid':
      case 'received':
        return {
          label: isPurchaseReturn ? 'RECEIVED' : 'PAID',
          color: 'success',
          icon: <VerifiedIcon sx={{ fontSize: 16 }} />,
          sx: {
            bgcolor: '#dcfce7',
            color: '#166534',
            fontWeight: 600,
            border: '1px solid #86efac',
            '& .MuiChip-icon': { color: '#166534' }
          }
        };
      case 'pending':
        return {
          label: 'PENDING',
          color: 'warning',
          sx: {
            bgcolor: '#fef9c3',
            color: '#854d0e',
            fontWeight: 600,
            border: '1px solid #fde047'
          }
        };
      case 'overdue':
        return {
          label: 'OVERDUE',
          color: 'error',
          sx: {
            bgcolor: '#fee2e2',
            color: '#991b1b',
            fontWeight: 600,
            border: '1px solid #fca5a5'
          }
        };
      default:
        return {
          label: status?.toUpperCase() || 'N/A',
          color: 'default',
          sx: {
            bgcolor: '#f1f5f9',
            color: colors.secondary,
            fontWeight: 600,
            border: `1px solid ${colors.border}`
          }
        };
    }
  };


  const hasItemLevelDiscount =
    invoice?.details?.some((item) => parseFloat(item.discountAmt || 0) > 0) ?? false;
  const hasInvoiceLevelDiscount =
    !hasItemLevelDiscount && parseFloat(invoice?.master?.discountAmt || 0) > 0;

  // Format currency values
  const formatCurrency = (value) => {
    return (
      <span style={{ display: 'inline-flex', alignItems: 'center' }}>
        {currencySymbol === 'SAR' && (
          <img src={image1} alt="SAR" style={{ width: '13px', height: '13px', marginRight: '4px' }} />
        )}
        {currencySymbol !== 'SAR' && (
          <span style={{ marginRight: '4px' }}>{currencySymbol}</span>
        )}
        {parseFloat(value || 0).toFixed(2).replace(/\B(?=(\d{3})+(?!\d))/g, ",")}
      </span>
    );
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      maxWidth="lg"
      fullWidth
      PaperProps={{
        sx: {
          borderRadius: 1,
          maxWidth: '95%',
          m: 1,
          boxShadow: '0px 10px 30px rgba(0, 0, 0, 0.1)',
          background: colors.background,
          position: 'relative',
          overflow: 'hidden'
        }
      }}
    >
      {/* Elegant header with title and close button */}
      <DialogTitle sx={{
        backgroundColor: colors.primary,
        color: 'white',
        py: 1.5,
        px: 3
      }}>
        <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <ReceiptIcon sx={{ mr: 1.5 }} />
            <Typography variant="h6" sx={{ fontWeight: 600, letterSpacing: 0.5 }}>
              {title}
            </Typography>
          </Box>
          <IconButton onClick={onClose} size="small" sx={{ color: 'white' }}>
            <CloseIcon />
          </IconButton>
        </Box>
      </DialogTitle>

      <DialogContent sx={{ p: 0, backgroundColor: colors.background }}>
        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', p: 5 }}>
            <CircularProgress sx={{ color: colors.primary }} />
          </Box>
        ) : invoice && invoice.master ? (
          <Box>
            {/* Invoice Header Section with watermark */}
            <Box sx={headerStyle}>
              <Box sx={watermarkStyle}>ZATCA<br></br>APPROVED</Box>

              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', mb: 2 }}>
                <Box>
                  <Typography variant="h5" sx={{ fontWeight: 700, color: colors.primary, mb: 0.5 }}>
                    INVOICE #{invoice.master.invoiceNo || '-'}
                  </Typography>
                  <Typography variant="subtitle2" sx={{ color: colors.secondary, fontWeight: 500 }}>
                    Issued on: {invoice.master.invoiceDate ? new Date(invoice.master.invoiceDate).toLocaleDateString('en-US', {
                      year: 'numeric',
                      month: 'long',
                      day: 'numeric'
                    }) : '-'}
                  </Typography>
                </Box>

                <Box sx={{ textAlign: 'right' }}>
                  {invoice.master.paymentStatus && (
                    <Chip
                      {...getStatusChipProps(invoice.master.paymentStatus)}
                      icon={getStatusChipProps(invoice.master.paymentStatus).icon}
                      size="medium"
                    />
                  )}
                </Box>
              </Box>

              <Grid container spacing={4} sx={{ mt: 1 }}>
                {/* Customer Information */}
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{
                    color: colors.secondary,
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    mb: 1
                  }}>
                    Customer Information
                  </Typography>
                  <Paper elevation={0} sx={{
                    p: 2,
                    bgcolor: colors.paper,
                    border: `1px solid ${colors.border}`,
                    borderRadius: 1
                  }}>
                    <Typography variant="body1" sx={{ fontWeight: 600, color: colors.primary, mb: 0.5 }}>
                      {invoice.master.customerName || '-'}
                    </Typography>
                    <Typography variant="body2" sx={{ color: colors.secondary, mb: 0.5 }}>
                      {invoice.master.customerType ? `Type: ${invoice.master.customerType}` : ''}
                    </Typography>
                    {invoice.master.customerPhone && (
                      <Typography variant="body2" sx={{ color: colors.secondary, mb: 0.5 }}>
                        Phone: {invoice.master.customerPhone}
                      </Typography>
                    )}
                    {invoice.master.customerAddress && (
                      <Typography variant="body2" sx={{ color: colors.secondary }}>
                        {invoice.master.customerAddress}
                      </Typography>
                    )}
                  </Paper>
                </Grid>

                {/* Invoice Summary */}
                <Grid item xs={12} md={6}>
                  <Typography variant="subtitle2" sx={{
                    color: colors.secondary,
                    fontWeight: 600,
                    textTransform: 'uppercase',
                    letterSpacing: 0.5,
                    mb: 1
                  }}>
                    Invoice Summary
                  </Typography>
                  <Paper elevation={0} sx={{
                    p: 2,
                    bgcolor: colors.paper,
                    border: `1px solid ${colors.border}`,
                    borderRadius: 1
                  }}>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" sx={{ color: colors.secondary }}>Sub Total:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {formatCurrency(invoice.master.subTotal)}
                      </Typography>
                    </Box>
                    {parseFloat(invoice.master.discountAmt || 0) > 0 && (
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                        <Typography variant="body2" sx={{ color: colors.secondary }}>
                          Discount{invoice.master.discountPerc > 0 ? ` (${invoice.master.discountPerc}%)` : ''}:
                        </Typography>
                        <Typography variant="body2" sx={{ fontWeight: 500, color: '#ef4444' }}>
                          - {formatCurrency(invoice.master.discountAmt)}
                        </Typography>
                      </Box>
                    )}
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 1 }}>
                      <Typography variant="body2" sx={{ color: colors.secondary }}>VAT Amount:</Typography>
                      <Typography variant="body2" sx={{ fontWeight: 500 }}>
                        {formatCurrency(invoice.master.vatAmt)}
                      </Typography>
                    </Box>
                    <Divider sx={{ my: 1.5, borderColor: colors.border }} />
                    <Box sx={{ display: 'flex', justifyContent: 'space-between' }}>
                      <Typography variant="subtitle2" sx={{ fontWeight: 600 }}>Total Amount:</Typography>
                      <Typography variant="subtitle1" sx={{ fontWeight: 700, color: colors.primary }}>
                        {formatCurrency(invoice.master.totalAmt)}
                      </Typography>
                    </Box>
                    {invoice.master.balanceAmt > 0 && (
                      <Box sx={{
                        display: 'flex',
                        justifyContent: 'space-between',
                        mt: 1,
                        pt: 1,
                        borderTop: `1px dashed ${colors.border}`
                      }}>
                        <Typography variant="subtitle2" sx={{ fontWeight: 600, color: '#ef4444' }}>
                          Balance Due:
                        </Typography>
                        <Typography variant="subtitle1" sx={{ fontWeight: 700, color: '#ef4444' }}>
                          {formatCurrency(invoice.master.balanceAmt)}
                        </Typography>
                      </Box>
                    )}
                  </Paper>
                </Grid>
              </Grid>
            </Box>

            {/* Invoice Items */}
            {invoice.details && Array.isArray(invoice.details) && invoice.details.length > 0 && (
              <Box sx={{ p: 3 }}>
                <Typography variant="h6" sx={{
                  fontWeight: 600,
                  color: colors.primary,
                  mb: 2,
                  position: 'relative',
                  display: 'inline-block',
                  '&:after': {
                    content: '""',
                    position: 'absolute',
                    width: '100%',
                    height: '3px',
                    left: 0,
                    bottom: '-8px',
                    background: `linear-gradient(90deg, ${colors.accent} 0%, ${colors.primary} 100%)`,
                    borderRadius: '2px'
                  }
                }}>
                  Products & Services
                </Typography>
                <TableContainer sx={{
                  borderRadius: 1,
                  overflow: 'hidden',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.05), 0 1px 2px rgba(0,0,0,0.1)'
                }}>
                  <Table sx={{
                    '& .MuiTableCell-root': {
                      py: 1.5,
                      px: 2
                    },
                    '& .MuiTableRow-root:nth-of-type(odd)': {
                      backgroundColor: alpha(colors.primary, 0.02),
                    }
                  }}>
                    <TableHead>
                      <TableRow sx={{
                        backgroundColor: colors.primary,
                        '& .MuiTableCell-root': {
                          color: '#1e3a8a',
                          fontWeight: 600,
                          fontSize: '0.875rem'
                        }
                      }}>
                        <TableCell>Item Code</TableCell>
                        <TableCell>Description</TableCell>
                        <TableCell align="right">Quantity</TableCell>
                        <TableCell align="right">Unit Price</TableCell>
                        <TableCell align="right">Discount</TableCell>
                        <TableCell align="right">VAT %</TableCell>
                        <TableCell align="right">VAT Amount</TableCell>
                        <TableCell align="right">Total</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {invoice.details.map((item, index) => (
                        <TableRow key={index} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                          <TableCell sx={{ color: colors.secondary, fontWeight: 500 }}>
                            {item.productCode || item.itemCode || '-'}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 500 }}>
                            {item.productName || item.description || '-'}
                          </TableCell>
                          <TableCell align="right">{item.qty || '0'}</TableCell>
                          <TableCell align="right">
                            {formatCurrency(item.unitPrice)}
                          </TableCell>
                          <TableCell align="right">
                            {hasItemLevelDiscount
                              ? formatCurrency(item.discountAmt ?? item.discount ?? 0)
                              : hasInvoiceLevelDiscount
                                ? formatCurrency(invoice.master.discountAmt)
                                : formatCurrency(0)}
                          </TableCell>
                          <TableCell align="right">{(item.vatPerc || item.vat || 0)}%</TableCell>
                          <TableCell align="right">
                            {formatCurrency(item.totalTax ?? item.vatAmount)}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>
                            {formatCurrency(item.totalAmount ?? item.grandTotal ?? item.totalPrice)}
                          </TableCell>
                        </TableRow>
                      ))}

                      {/* Total row */}
                      <TableRow sx={{
                        backgroundColor: alpha(colors.primary, 0.05),
                        '& .MuiTableCell-root': {
                          borderTop: `2px solid ${colors.border}`,
                          py: 1.5
                        }
                      }}>
                        <TableCell colSpan={6} />
                        <TableCell align="right" sx={{ fontWeight: 600, color: colors.primary }}>
                          Grand Total:
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: colors.primary }}>
                          {formatCurrency(invoice.master.totalAmt)}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {/* Payment History */}
            {invoice.payments && Array.isArray(invoice.payments) && invoice.payments.length > 0 && (
              <Box sx={{ px: 3, pb: 3 }}>
                <Typography variant="h6" sx={{
                  fontWeight: 600,
                  color: colors.primary,
                  mb: 2,
                  position: 'relative',
                  display: 'inline-block',
                  '&:after': {
                    content: '""',
                    position: 'absolute',
                    width: '100%',
                    height: '3px',
                    left: 0,
                    bottom: '-8px',
                    background: `linear-gradient(90deg, ${colors.accent} 0%, ${colors.primary} 100%)`,
                    borderRadius: '2px'
                  }
                }}>
                  Payment History
                </Typography>
                <TableContainer sx={{
                  borderRadius: 1,
                  overflow: 'hidden',
                  boxShadow: '0 1px 3px rgba(0,0,0,0.05), 0 1px 2px rgba(0,0,0,0.1)'
                }}>
                  <Table sx={{
                    '& .MuiTableCell-root': {
                      py: 1.5,
                      px: 2
                    },
                    '& .MuiTableRow-root:nth-of-type(odd)': {
                      backgroundColor: alpha(colors.primary, 0.02),
                    }
                  }}>
                    <TableHead>
                      <TableRow sx={{
                        backgroundColor: colors.primary,
                        '& .MuiTableCell-root': {
                          color: '#1e3a8a',
                          fontWeight: 600,
                          fontSize: '0.875rem'
                        }
                      }}>
                        <TableCell>Payment Date</TableCell>
                        <TableCell>Payment Method</TableCell>
                        <TableCell>Reference</TableCell>
                        <TableCell align="right">Amount</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {invoice.payments.map((payment, index) => (
                        <TableRow key={index} sx={{ '&:last-child td, &:last-child th': { border: 0 } }}>
                          <TableCell>
                            {payment.paymentDate ?
                              new Date(payment.paymentDate).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric'
                              }) :
                              (payment.date ? new Date(payment.date).toLocaleDateString('en-US', {
                                year: 'numeric',
                                month: 'short',
                                day: 'numeric'
                              }) : '-')}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 500 }}>
                            {payment.paymentMethod || payment.method || '-'}
                          </TableCell>
                          <TableCell sx={{ fontWeight: 500 }}>
                            {invoice.master.referenceNo || invoice.master.refInvoiceNo || '-'}
                          </TableCell>
                          <TableCell align="right" sx={{ fontWeight: 600 }}>
                            {formatCurrency(payment.amount)}
                          </TableCell>
                        </TableRow>
                      ))}

                      {/* Total payments row */}
                      <TableRow sx={{
                        backgroundColor: alpha(colors.primary, 0.05),
                        '& .MuiTableCell-root': {
                          borderTop: `2px solid ${colors.border}`,
                          py: 1.5
                        }
                      }}>
                        <TableCell colSpan={2} />
                        <TableCell align="right" sx={{ fontWeight: 600, color: colors.primary }}>
                          {title?.toLowerCase().includes('purchase return') || title?.toLowerCase().includes('return') ? 'Total Received:' : 'Total Paid:'}
                        </TableCell>
                        <TableCell align="right" sx={{ fontWeight: 700, color: colors.primary }}>
                          {formatCurrency(
                            invoice.payments.reduce((sum, payment) => sum + (parseFloat(payment.amount) || 0), 0)
                          )}
                        </TableCell>
                      </TableRow>
                    </TableBody>
                  </Table>
                </TableContainer>
              </Box>
            )}

            {/* Certification Footer */}
            <Box sx={{
              px: 3,
              py: 3,
              borderTop: `1px solid ${colors.border}`,
              backgroundColor: alpha(colors.primary, 0.02),
              textAlign: 'center'
            }}>
              <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', mb: 1 }}>
                <VerifiedIcon sx={{ color: '#10b981', mr: 1 }} />
                <Typography variant="subtitle2" sx={{
                  color: colors.primary,
                  fontWeight: 600,
                  letterSpacing: 1,
                  textTransform: 'uppercase'
                }}>
                  ZATCA Official Certified Document
                </Typography>
              </Box>
              <Typography variant="caption" sx={{ color: colors.secondary }}>
                This document serves as an official record of transaction and payment between the parties involved.
                <Box component="span" sx={{ display: 'inline-flex', alignItems: 'center', gap: '4px', ml: 0.5 }}>
                  All amounts are shown in <img src={currencyIcon || image1} alt="currency" style={{ width: '15px', height: '15px', objectFit: 'contain' }} />.
                </Box>
              </Typography>
            </Box>
          </Box>
        ) : (
          <Box sx={{ p: 5, textAlign: 'center' }}>
            <Typography variant="subtitle1" sx={{ color: colors.secondary }}>
              No valid invoice data available
            </Typography>
          </Box>
        )}
      </DialogContent>

      <DialogActions sx={{
        p: 2,
        borderTop: `1px solid ${colors.border}`,
        backgroundColor: colors.paper,
        justifyContent: 'space-between'
      }}>
        <Button
          onClick={onClose}
          variant="outlined"
          sx={{
            borderRadius: 0.5,
            borderColor: colors.border,
            color: colors.secondary,
            textTransform: 'uppercase',
            letterSpacing: 0.5,
            fontWeight: 600,
            '&:hover': {
              borderColor: colors.secondary,
              backgroundColor: alpha(colors.secondary, 0.05)
            }
          }}
        >
          Close
        </Button>

      </DialogActions>
    </Dialog>
  );
};

export default InvoiceViewDialog;